/**
 * catalogService.client — Implementazione frontend (firebase/firestore client SDK)
 * delle funzioni di lettura/scrittura catalogo.
 *
 * Usato da console-app, preventivatore-app, landing-app.
 *
 * @since 2026-05-17
 */

import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  type DocumentData,
  type Firestore,
  type UpdateData,
} from 'firebase/firestore';
import type {
  ModuleDescriptor,
  FeatureDescriptor,
  SettingDescriptor,
} from '../types/catalog.types';
import {
  CATALOG_COLLECTIONS,
  getCachedCatalog,
  setCachedCatalog,
  invalidateCatalogCache,
  type CatalogSnapshot,
} from './catalogService';

/**
 * Carica l'intero catalogo da Firestore (3 query parallele).
 * Usa cache in-memory con TTL 5 min (overridabile con `force=true`).
 */
export async function loadCatalog(
  db: Firestore,
  opts: { force?: boolean } = {},
): Promise<CatalogSnapshot> {
  if (!opts.force) {
    const cached = getCachedCatalog();
    if (cached) return cached;
  }

  const [modulesSnap, featuresSnap, settingsSnap] = await Promise.all([
    getDocs(collection(db, CATALOG_COLLECTIONS.modules)),
    getDocs(collection(db, CATALOG_COLLECTIONS.features)),
    getDocs(collection(db, CATALOG_COLLECTIONS.settings)),
  ]);

  const snapshot: CatalogSnapshot = {
    modules: modulesSnap.docs.map((d) => ({ ...d.data(), id: d.id } as ModuleDescriptor)),
    features: featuresSnap.docs.map((d) => ({ ...d.data(), id: d.id } as FeatureDescriptor)),
    settings: settingsSnap.docs.map((d) => ({ ...d.data(), id: d.id } as SettingDescriptor)),
    loaded_at: Date.now(),
  };

  // Sort by label per UX consistente
  snapshot.modules.sort((a, b) => a.label.localeCompare(b.label, 'it'));
  snapshot.features.sort((a, b) => a.label.localeCompare(b.label, 'it'));
  snapshot.settings.sort((a, b) => a.label.localeCompare(b.label, 'it'));

  setCachedCatalog(snapshot);
  return snapshot;
}

/**
 * Aggiorna un singolo item del catalogo (solo campi modificabili).
 * Solo super_admin (gating Firestore rules). Invalida la cache.
 */
export async function updateCatalogItem(
  db: Firestore,
  type: 'module' | 'feature' | 'setting',
  itemId: string,
  patch: Partial<ModuleDescriptor | FeatureDescriptor | SettingDescriptor>,
  updaterUid: string,
): Promise<void> {
  const collectionName =
    type === 'module' ? CATALOG_COLLECTIONS.modules :
    type === 'feature' ? CATALOG_COLLECTIONS.features :
    CATALOG_COLLECTIONS.settings;

  // Whitelist campi modificabili (evita di rovinare schema/audit)
  const allowedFields = new Set([
    'label', 'description', 'category', 'status', 'pricing_hint',
    'requires', 'alternatives', 'parent_module', 'sub_sections',
    'storage_path', 'scope', 'controls_marketplace_menu', 'standalone_app',
  ]);
  // Tipo del payload = quello che il client SDK richiede per `updateDoc` su un
  // `DocumentReference<DocumentData>`: chiavi dinamiche (la whitelist qui sopra) + i
  // sentinel `FieldValue` (serverTimestamp). NB: con `Record<string, unknown>`
  // TypeScript inferiva `DbModelType` dal payload stesso (ramo `Partial<T>` del
  // tipo condizionale `UpdateData`) e poi rifiutava i valori `unknown`.
  const cleanedPatch: UpdateData<DocumentData> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (allowedFields.has(k) && v !== undefined) {
      cleanedPatch[k] = v;
    }
  }

  cleanedPatch.updated_at = serverTimestamp();
  cleanedPatch.updated_by_uid = updaterUid;

  await updateDoc(doc(db, collectionName, itemId), cleanedPatch);
  invalidateCatalogCache();
}

export { invalidateCatalogCache };
